import { useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Tag, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useEnvelopeBudgets } from '@/hooks/budgets/useEnvelopeBudgets';
import { useBudgetCategories } from '@/hooks/budgets/useBudgetCategories';
import { useBudgetCategoryGroups } from '@/hooks/budgets/useBudgetCategoryGroups';
import { BudgetCategory, BudgetCategoryGroup, EnvelopeBudget } from '@/types/budgets';

interface BudgetCategoryPickerProps {
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
}

/** Multi-select picker for budget categories across all active budgets */
export function BudgetCategoryPicker({ selectedCategoryIds, onChange }: BudgetCategoryPickerProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const [open, setOpen] = useState(false);

  const { data: budgets, isLoading: budgetsLoading } = useEnvelopeBudgets(orgId);
  const activeBudgets = useMemo(
    () => (budgets ?? []).filter((b) => b.status === 'active'),
    [budgets]
  );

  // Don't render anything if there are no active budgets (after loading completes)
  if (!budgetsLoading && !activeBudgets.length) return null;

  const toggleCategory = (categoryId: string) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onChange([...selectedCategoryIds, categoryId]);
    }
  };

  const removeCategory = (categoryId: string) => {
    onChange(selectedCategoryIds.filter((id) => id !== categoryId));
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs gap-1" disabled={budgetsLoading}>
            <Tag className="h-3 w-3" />
            {budgetsLoading
              ? '...'
              : selectedCategoryIds.length === 0
                ? t('budgets.tagCategory')
                : t('budgets.categoriesTagged', { count: selectedCategoryIds.length })}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0 max-h-[300px] overflow-y-auto" align="start">
          {activeBudgets.map((budget) => (
            <BudgetSection
              key={budget.id}
              budget={budget}
              selectedCategoryIds={selectedCategoryIds}
              onToggle={toggleCategory}
            />
          ))}
        </PopoverContent>
      </Popover>

      {selectedCategoryIds.length > 0 && (
        <SelectedBadges
          budgets={activeBudgets}
          selectedCategoryIds={selectedCategoryIds}
          onRemove={removeCategory}
        />
      )}
    </div>
  );
}

function BudgetSection({
  budget,
  selectedCategoryIds,
  onToggle,
}: {
  budget: EnvelopeBudget;
  selectedCategoryIds: string[];
  onToggle: (id: string) => void;
}) {
  const { data: groups } = useBudgetCategoryGroups(budget.id);
  const { data: categories } = useBudgetCategories(budget.id);

  if (!categories?.length) return null;

  const categoriesByGroup = (groupId: string) =>
    (categories ?? []).filter((c) => c.group_id === groupId && !c.is_hidden);

  return (
    <div className="border-b last:border-b-0">
      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/30">
        {budget.name}
      </div>
      {(groups ?? []).map((group) => {
        const groupCats = categoriesByGroup(group.id);
        if (!groupCats.length) return null;
        return (
          <div key={group.id}>
            <div className="px-3 py-1 text-xs text-muted-foreground">{group.name}</div>
            {groupCats.map((cat) => (
              <label
                key={cat.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedCategoryIds.includes(cat.id)}
                  onCheckedChange={() => onToggle(cat.id)}
                />
                {cat.name}
              </label>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SelectedBadges({
  budgets,
  selectedCategoryIds,
  onRemove,
}: {
  budgets: EnvelopeBudget[];
  selectedCategoryIds: string[];
  onRemove: (id: string) => void;
}) {
  // Build a lookup of category id -> name from all budgets
  return (
    <>
      {budgets.map((budget) => (
        <BudgetBadges
          key={budget.id}
          budgetId={budget.id}
          selectedCategoryIds={selectedCategoryIds}
          onRemove={onRemove}
        />
      ))}
    </>
  );
}

function BudgetBadges({
  budgetId,
  selectedCategoryIds,
  onRemove,
}: {
  budgetId: string;
  selectedCategoryIds: string[];
  onRemove: (id: string) => void;
}) {
  const { data: categories } = useBudgetCategories(budgetId);

  const selected = useMemo(
    () => (categories ?? []).filter((c) => selectedCategoryIds.includes(c.id)),
    [categories, selectedCategoryIds]
  );

  if (!selected.length) return null;

  return (
    <>
      {selected.map((cat) => (
        <Badge key={cat.id} variant="secondary" className="text-xs gap-1 h-5">
          {cat.name}
          <button type="button" onClick={() => onRemove(cat.id)} className="hover:text-destructive">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
    </>
  );
}